import type { CustomerInfo } from '../../store/useQuoteStore';
import { PhoneInput, PostalInput } from '../ui';

type FieldErrors = Partial<Record<keyof CustomerInfo, string>>;

interface PrimaryAddressBlockProps {
  customer: CustomerInfo;
  errors: FieldErrors;
  onChange: (field: keyof CustomerInfo, value: string | boolean) => void;
  onBlur: (field: keyof CustomerInfo) => void;
  postalStatus: string;
  isSearching: boolean;
}

export function PrimaryAddressBlock({
  customer,
  errors,
  onChange,
  onBlur,
  postalStatus,
  isSearching,
}: PrimaryAddressBlockProps) {
  return (
    <>
      <label className="flex flex-col gap-2 md:col-span-2">
        <span className="text-xs font-bold text-outline uppercase tracking-wider">
          Customer Name
        </span>
        <input
          type="text"
          value={customer.name}
          onChange={(e) => onChange('name', e.target.value)}
          onBlur={() => onBlur('name')}
          className={`border bg-background rounded-lg p-3 text-sm focus:border-primary focus:ring-1 outline-none text-on-surface ${errors.name ? 'border-red-500' : 'border-outline-variant'}`}
          placeholder="e.g. John Tan"
        />
        {errors.name && <span className="text-xs text-red-500">{errors.name}</span>}
      </label>

      <label className="flex flex-col gap-2">
        <span className="text-xs font-bold text-outline uppercase tracking-wider">
          Mobile Number
        </span>
        <PhoneInput
          value={customer.mobile}
          onChange={(v) => onChange('mobile', v)}
          onBlur={() => onBlur('mobile')}
          className={`border bg-background rounded-lg p-3 text-sm focus:border-primary focus:ring-1 outline-none ${errors.mobile ? 'border-red-500' : 'border-outline-variant'}`}
          placeholder="e.g. 9123 4567 (+code overseas)"
        />
        {errors.mobile && <span className="text-xs text-red-500">{errors.mobile}</span>}
      </label>

      <label className="flex flex-col gap-2">
        <span className="text-xs font-bold text-outline uppercase tracking-wider">
          Email <span className="text-outline/50 font-normal normal-case">(optional)</span>
        </span>
        <input
          type="email"
          value={customer.email ?? ''}
          onChange={(e) => onChange('email', e.target.value)}
          onBlur={() => onBlur('email')}
          className={`border bg-background rounded-lg p-3 text-sm focus:border-primary focus:ring-1 outline-none ${errors.email ? 'border-red-500' : 'border-outline-variant'}`}
          placeholder="e.g. customer@example.com"
        />
        {errors.email && <span className="text-xs text-red-500">{errors.email}</span>}
      </label>

      <label className="flex flex-col gap-2">
        <span className="text-xs font-bold text-outline uppercase tracking-wider">
          Postal Code <span className="text-outline/50 font-normal normal-case">(optional)</span>
        </span>
        <div className="relative">
          <PostalInput
            value={customer.postal}
            onChange={(v) => onChange('postal', v)}
            onBlur={() => onBlur('postal')}
            className={`border bg-background rounded-lg p-3 text-sm focus:border-primary focus:ring-1 outline-none w-full ${errors.postal ? 'border-red-500' : 'border-outline-variant'}`}
            placeholder="e.g. 521123"
          />
          {isSearching && (
            <span className="absolute right-3 top-3 text-xs text-outline">Searching...</span>
          )}
        </div>
        {errors.postal ? (
          <span className="text-xs text-red-500">{errors.postal}</span>
        ) : (
          postalStatus && (
            <span
              className={`text-xs font-medium ${postalStatus.startsWith('✓') ? 'text-green-600' : 'text-red-500'}`}
            >
              {postalStatus}
            </span>
          )
        )}
      </label>

      <label className="flex flex-col gap-2">
        <span className="text-xs font-bold text-outline uppercase tracking-wider">
          Unit Number <span className="text-outline/50 font-normal normal-case">(optional)</span>
        </span>
        <input
          type="text"
          value={customer.unit}
          onChange={(e) => onChange('unit', e.target.value)}
          className="border border-outline-variant bg-background rounded-lg p-3 text-sm focus:border-primary focus:ring-1 outline-none uppercase"
          placeholder="e.g. #01-23"
        />
      </label>

      <label className="flex flex-col gap-2 md:col-span-2">
        <span className="text-xs font-bold text-outline uppercase tracking-wider">
          Street Address
        </span>
        <div className="flex gap-2">
          <input
            type="text"
            readOnly
            value={customer.blk}
            className="border border-outline-variant bg-surface-container-highest rounded-lg p-3 text-sm text-outline w-1/4 outline-none"
            placeholder="Blk No."
          />
          <input
            type="text"
            readOnly
            value={customer.street}
            className="border border-outline-variant bg-surface-container-highest rounded-lg p-3 text-sm text-outline w-2/4 outline-none"
            placeholder="Street Name"
          />
          <input
            type="text"
            value={customer.building}
            onChange={(e) => onChange('building', e.target.value)}
            className="border border-outline-variant bg-background rounded-lg p-3 text-sm text-on-surface w-1/4 focus:border-primary focus:ring-1 outline-none"
            placeholder="Building"
          />
        </div>
      </label>
    </>
  );
}

interface InstallationAddressBlockProps {
  customer: CustomerInfo;
  errors: FieldErrors;
  onChange: (field: keyof CustomerInfo, value: string | boolean) => void;
  onBlur: (field: keyof CustomerInfo) => void;
  installPostalStatus: string;
  isSearchingInstall: boolean;
}

export function InstallationAddressBlock({
  customer,
  errors,
  onChange,
  onBlur,
  installPostalStatus,
  isSearchingInstall,
}: InstallationAddressBlockProps) {
  return (
    <div className="md:col-span-2 bg-surface-container-lowest border border-outline-variant rounded-xl p-6 mt-4">
      <h3 className="text-sm font-bold text-secondary mb-4 flex items-center gap-2">
        <span className="material-symbols-outlined text-[18px]">location_on</span>
        Installation Site Details
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
        <label className="flex flex-col gap-2">
          <span className="text-xs font-bold text-outline uppercase tracking-wider">
            Site Contact Name <span className="text-red-500">*</span>
          </span>
          <input
            type="text"
            value={customer.installName ?? ''}
            onChange={(e) => onChange('installName', e.target.value)}
            onBlur={() => onBlur('installName')}
            className={`border bg-white rounded-lg p-3 text-sm focus:border-primary focus:ring-1 outline-none ${errors.installName ? 'border-red-500' : 'border-outline-variant'}`}
            placeholder="e.g. Site Manager"
          />
          {errors.installName && <span className="text-xs text-red-500">{errors.installName}</span>}
        </label>

        <label className="flex flex-col gap-2">
          <span className="text-xs font-bold text-outline uppercase tracking-wider">
            Site Mobile <span className="text-red-500">*</span>
          </span>
          <PhoneInput
            value={customer.installMobile ?? ''}
            onChange={(v) => onChange('installMobile', v)}
            onBlur={() => onBlur('installMobile')}
            className={`border bg-white rounded-lg p-3 text-sm focus:border-primary focus:ring-1 outline-none ${errors.installMobile ? 'border-red-500' : 'border-outline-variant'}`}
            placeholder="e.g. 9123 4567 (+code overseas)"
          />
          {errors.installMobile && (
            <span className="text-xs text-red-500">{errors.installMobile}</span>
          )}
        </label>

        <label className="flex flex-col gap-2">
          <span className="text-xs font-bold text-outline uppercase tracking-wider">
            Site Postal Code <span className="text-red-500">*</span>
          </span>
          <div className="relative">
            <PostalInput
              value={customer.installPostal ?? ''}
              onChange={(v) => onChange('installPostal', v)}
              onBlur={() => onBlur('installPostal')}
              className={`border bg-white rounded-lg p-3 text-sm focus:border-primary focus:ring-1 outline-none w-full ${errors.installPostal ? 'border-red-500' : 'border-outline-variant'}`}
              placeholder="e.g. 521123"
            />
            {isSearchingInstall && (
              <span className="absolute right-3 top-3 text-xs text-outline">Searching...</span>
            )}
          </div>
          {errors.installPostal ? (
            <span className="text-xs text-red-500">{errors.installPostal}</span>
          ) : (
            installPostalStatus && (
              <span
                className={`text-xs font-medium ${installPostalStatus.startsWith('✓') ? 'text-green-600' : 'text-red-500'}`}
              >
                {installPostalStatus}
              </span>
            )
          )}
        </label>

        <label className="flex flex-col gap-2">
          <span className="text-xs font-bold text-outline uppercase tracking-wider">
            Unit Number <span className="text-outline/50 font-normal normal-case">(optional)</span>
          </span>
          <input
            type="text"
            value={customer.installUnit ?? ''}
            onChange={(e) => onChange('installUnit', e.target.value)}
            className="border border-outline-variant bg-white rounded-lg p-3 text-sm focus:border-primary focus:ring-1 outline-none uppercase"
            placeholder="e.g. #01-23"
          />
        </label>

        <label className="flex flex-col gap-2 md:col-span-2">
          <span className="text-xs font-bold text-outline uppercase tracking-wider">
            Site Street Address
          </span>
          <div className="flex gap-2">
            <input
              type="text"
              readOnly
              value={customer.installBlk ?? ''}
              className="border border-outline-variant bg-surface-container-highest rounded-lg p-3 text-sm text-outline w-1/4 outline-none"
              placeholder="Blk No."
            />
            <input
              type="text"
              readOnly
              value={customer.installStreet ?? ''}
              className="border border-outline-variant bg-surface-container-highest rounded-lg p-3 text-sm text-outline w-2/4 outline-none"
              placeholder="Street Name"
            />
            <input
              type="text"
              value={customer.installBuilding ?? ''}
              onChange={(e) => onChange('installBuilding', e.target.value)}
              className="border border-outline-variant bg-white rounded-lg p-3 text-sm text-on-surface w-1/4 focus:border-primary focus:ring-1 outline-none"
              placeholder="Building"
            />
          </div>
        </label>
      </div>
    </div>
  );
}
